import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import API, { BASE_URL } from '../../../../../Api/api';
import PropertyCard from './PropertyCard';
import AppIcon from '../../../../../Components/AppIcon';
import './CompanyPropertiesPage.css';

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 300;

const logoSrc = (logo) => {
  if (!logo) return '';
  const s = String(logo);
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(BASE_URL || '').replace(/\/+$/, '');
  const path = s.replace(/^\/+/, '');
  return `${base}/${path.startsWith('uploads/') ? path : `uploads/${path}`}`;
};

const listingTitle = (p) =>
  p?.extra_fields?.property_name
  || p?.extra_fields?.project_name
  || p?.seo_title
  || p?.location
  || p?.map_location_name
  || `#${p?.property_id || p?.id || ''}`;

const listingSearchText = (p) => [
  listingTitle(p),
  p?.location,
  p?.map_location_name,
  p?.address,
  p?.seo_title,
  p?.listing_type,
  p?.company_name,
  p?.property_id,
  p?.id,
  p?.extra_fields?.property_name,
  p?.extra_fields?.project_name,
  p?.extra_fields?.city,
  p?.extra_fields?.locality,
]
  .filter((v) => v != null && String(v).trim() !== '')
  .join(' ')
  .toLowerCase();

const isProjectListing = (p) => p?.is_project === true || p?.is_project === 1 || p?.is_project === '1';

const CompanyPropertiesPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [company, setCompany] = useState(null);
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kind, setKind] = useState('all'); // all | property | project
  const [sort, setSort] = useState('newest'); // newest | oldest | price_asc | price_desc
  const [page, setPage] = useState(1);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      // Always load the full company catalog; filter/sort locally for reliability
      const res = await API.get(`/companies/${id}`, { signal });
      if (signal?.aborted) return;

      setCompany(res.data?.company || null);
      const mixed = Array.isArray(res.data?.listings) && res.data.listings.length
        ? res.data.listings
        : [
          ...(Array.isArray(res.data?.properties) ? res.data.properties : []),
          ...(Array.isArray(res.data?.projects) ? res.data.projects : []),
        ];
      setListings(mixed);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || signal?.aborted) return;
      setError(e.response?.data?.error || 'Company not found');
      setCompany(null);
      setListings([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const ac = new AbortController();
    setSearch('');
    setDebouncedSearch('');
    setKind('all');
    setSort('newest');
    setPage(1);
    load(ac.signal);
    window.scrollTo(0, 0);
    return () => ac.abort();
  }, [load]);

  useEffect(() => {
    if (company?.name) {
      document.title = `${company.name} | Properties Infinite`;
    }
  }, [company?.name]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, kind, sort, id]);

  const filtered = useMemo(() => {
    let rows = Array.isArray(listings) ? [...listings] : [];

    if (kind === 'property') {
      rows = rows.filter((p) => !isProjectListing(p));
    } else if (kind === 'project') {
      rows = rows.filter((p) => isProjectListing(p));
    }

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      const qDigits = q.replace(/^#\s*/, '');
      rows = rows.filter((p) => {
        const hay = listingSearchText(p);
        if (hay.includes(q)) return true;
        if (qDigits && /^\d+$/.test(qDigits)) {
          const idStr = String(p?.property_id ?? p?.id ?? '');
          if (idStr === qDigits || idStr.startsWith(qDigits)) return true;
        }
        return false;
      });
    }

    rows.sort((a, b) => {
      if (sort === 'oldest') {
        return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      }
      if (sort === 'price_asc') {
        return (Number(a.price) || 0) - (Number(b.price) || 0);
      }
      if (sort === 'price_desc') {
        return (Number(b.price) || 0) - (Number(a.price) || 0);
      }
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });

    return rows;
  }, [listings, kind, debouncedSearch, sort]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
  const safePage = Math.min(page, pages);
  const pageRows = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage]
  );
  const from = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(safePage * PAGE_SIZE, total);

  const initials = String(company?.name || 'C')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();

  const listingCount = listings.length;
  const joinedLabel = (() => {
    const d = company?.createdAt ? new Date(company.createdAt) : null;
    if (!d || Number.isNaN(d.getTime())) return 'Joined recently';
    return `Joined ${d.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`;
  })();
  const descriptionText = String(company?.description || '').trim()
    || 'Building better spaces for a brighter tomorrow. We develop high-quality residential and commercial properties with a focus on trust, transparency, and customer satisfaction.';
  const headerPhoto =
    company?.effective_header_image_url
    || (company?.effective_header_image ? logoSrc(company.effective_header_image) : '')
    || company?.header_image_url
    || (company?.header_image ? logoSrc(company.header_image) : '');

  const clearSearch = () => {
    setSearch('');
    setDebouncedSearch('');
  };

  const resultLabel = (() => {
    if (total > 0) {
      const qNote = debouncedSearch ? ` for “${debouncedSearch}”` : '';
      return `Showing ${from}–${to} of ${total} listing${total === 1 ? '' : 's'}${qNote}`;
    }
    if (debouncedSearch || kind !== 'all') {
      return 'No listings match your search or filters';
    }
    return 'No listings match your filters';
  })();

  return (
    <div className="company-props-page">
      {loading ? (
        <div className="company-props-hero-loading">
          <div className="company-props-skeleton" />
        </div>
      ) : error ? (
        <div className="company-props-hero-loading">
          <div className="company-props-empty">
            <h1>Company not found</h1>
            <p>{error}</p>
            <Link to="/properties" className="company-props-link">Browse all properties</Link>
          </div>
        </div>
      ) : (
        <section className={`company-props-header-section${headerPhoto ? ' has-photo' : ''}`}>
          <header className="company-props-header">
            <div className="company-props-header-grid">
              <div className="company-props-header-panel">
                <div className="company-props-header-panel-bg" aria-hidden="true">
                  <span className="company-props-deco-orb company-props-deco-orb--1" />
                  <span className="company-props-deco-orb company-props-deco-orb--2" />
                  <span className="company-props-deco-orb company-props-deco-orb--3" />
                  <span className="company-props-deco company-props-deco--a">
                    <AppIcon name="home" size={15} />
                  </span>
                  <span className="company-props-deco company-props-deco--b">
                    <AppIcon name="key" size={14} />
                  </span>
                  <span className="company-props-deco company-props-deco--c">
                    <AppIcon name="mapPin" size={14} />
                  </span>
                  <span className="company-props-deco company-props-deco--d">
                    <AppIcon name="search" size={13} />
                  </span>
                  <span className="company-props-deco company-props-deco--e">
                    <AppIcon name="star" size={13} />
                  </span>
                </div>
                <div className="company-props-header-panel-inner">
                  <nav className="company-props-crumbs" aria-label="Breadcrumb">
                    <Link to="/">Home</Link>
                    <span aria-hidden="true">/</span>
                    <span>{company?.name}</span>
                  </nav>

                  <div className="company-props-header-main">
                    <div className="company-props-logo-wrap">
                      {company?.logo_url || company?.logo ? (
                        <img
                          src={company.logo_url || logoSrc(company.logo)}
                          alt=""
                          className="company-props-logo-img"
                        />
                      ) : (
                        <span className="company-props-logo-fallback">{initials || 'C'}</span>
                      )}
                    </div>

                    <div className="company-props-header-info">
                      <div className="company-props-title-row">
                        <h1 className="company-props-name">{company?.name}</h1>
                        <span className="company-props-badge">
                          <AppIcon name="verified" size={14} /> Verified
                        </span>
                      </div>
                      <p className="company-props-desc">{descriptionText}</p>
                      <div className="company-props-meta">
                        <span className="company-props-meta-item">
                          <AppIcon name="home" size={14} />
                          {listingCount} {listingCount === 1 ? 'Property' : 'Properties'}
                        </span>
                        <span className="company-props-meta-item">
                          <AppIcon name="calendar" size={14} />
                          {joinedLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {headerPhoto ? (
                <aside className="company-props-header-visual" aria-hidden="true">
                  <div
                    className="company-props-visual-photo"
                    style={{ backgroundImage: `url("${headerPhoto}")` }}
                  >
                    <div className="company-props-visual-shade" />
                  </div>

                  <svg
                    className="company-props-visual-overlay"
                    viewBox="0 0 1000 1000"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <line
                      x1="180"
                      y1="0"
                      x2="0"
                      y2="1000"
                      fill="none"
                      stroke="#c4a35a"
                      strokeWidth="5"
                      strokeLinecap="square"
                    />
                  </svg>

                  <div className="company-props-header-tagline">
                    <span>Spaces for a better</span>
                    <strong>Tomorrow</strong>
                  </div>
                </aside>
              ) : null}
            </div>
          </header>

          <div className="company-props-header-toolbar">
            <div className="company-props-kind" role="group" aria-label="Listing type">
              {[
                { id: 'all', label: 'All' },
                { id: 'property', label: 'Property' },
                { id: 'project', label: 'Project' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={kind === tab.id ? 'is-active' : ''}
                  onClick={() => setKind(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="company-props-toolbar-right">
              <div className="company-props-search">
                <AppIcon name="search" size={16} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, ID, location…"
                  aria-label="Search company listings"
                />
                {search ? (
                  <button
                    type="button"
                    className="company-props-search-clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </div>

              <label className="company-props-sort">
                <span>Sort by</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
              </label>
            </div>
          </div>
        </section>
      )}

      {!loading && !error && (
        <div className="company-props-body">
          <div className="company-props-result-meta">
            {resultLabel}
          </div>

          {pageRows.length === 0 ? (
            <div className="company-props-empty-soft">
              {!debouncedSearch && kind === 'all'
                ? 'No approved properties or projects are linked to this company yet.'
                : 'Try a different search or filter.'}
              {!debouncedSearch && kind === 'all' ? (
                <div>
                  <Link to="/properties" className="company-props-link">Browse all properties</Link>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="company-props-grid">
              {pageRows.map((p) => (
                <PropertyCard
                  key={`${isProjectListing(p) ? 'proj' : 'prop'}-${p.id || p.property_id}`}
                  property={p}
                />
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="company-props-pagination">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {safePage} of {pages}
              </span>
              <button
                type="button"
                disabled={safePage >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyPropertiesPage;
